import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import {
  brokers,
  type Broker,
  type BrokerConsultationCommissionCheck,
  type BrokerConsultationLimitInput,
  type BrokerConsultationPeriod,
  type BrokerConsultationStatus,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatDateTime,
  formatRate,
  parseRate,
  rateInputError,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { chaveDoLimiteDeConsultas, useLimiteDeConsultas } from './limiteDeConsultas';
import {
  PENDENCIA_COMISSAO_VALIDACAO,
  PENDENCIA_CONTAGEM_CONSULTA,
  PENDENCIA_LIMITE_CONSULTA,
  PERMISSAO_DESBLOQUEIO_CONSULTA,
  PERMISSAO_LIMITE_CONSULTA,
  periodosDoLimite,
  resultadosDaComissao,
  rotuloDoPeriodo,
} from './rotulos';

function situacaoDoPeriodo(situacao: BrokerConsultationStatus) {
  if (!situacao.configured || !situacao.limit) {
    return <Badge tone="neutral">Sem limite</Badge>;
  }
  if (!situacao.limit.active) return <Badge tone="neutral">Limite inativo</Badge>;
  if (situacao.blocked) return <Badge tone="danger">Bloqueado</Badge>;
  if (situacao.reached) return <Badge tone="warning">Limite atingido</Badge>;
  return <Badge tone="success">Dentro do limite</Badge>;
}

function LimitForm({
  open,
  atual,
  enviando,
  erro,
  onClose,
  onEnviar,
}: {
  open: boolean;
  atual: BrokerConsultationStatus['limit'];
  enviando: boolean;
  erro: string | undefined;
  onClose: () => void;
  onEnviar: (entrada: BrokerConsultationLimitInput) => void;
}) {
  const [quantidade, setQuantidade] = useState(atual ? String(atual.max_queries) : '');
  const [periodo, setPeriodo] = useState<BrokerConsultationPeriod>(
    atual?.period ?? 'mensal',
  );
  const [bloqueio, setBloqueio] = useState(atual?.auto_block ?? false);
  const [ativo, setAtivo] = useState(atual?.active ?? true);
  const [observacoes, setObservacoes] = useState(atual?.notes ?? '');

  const valida = /^\d{1,7}$/.test(quantidade.trim());
  const erroQuantidade =
    quantidade.trim() !== '' && !valida
      ? 'Informe um numero inteiro de consultas.'
      : undefined;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!valida) return;
    onEnviar({
      max_queries: Number(quantidade.trim()),
      period: periodo,
      auto_block: bloqueio,
      active: ativo,
      notes: observacoes,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={atual ? 'Alterar limite de consultas' : 'Cadastrar limite de consultas'}
      description={PENDENCIA_LIMITE_CONSULTA}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="limite-de-consultas"
            loading={enviando}
            disabled={!valida}
          >
            Salvar limite
          </Button>
        </>
      }
    >
      <form id="limite-de-consultas" onSubmit={enviar} className="flex flex-col gap-3">
        {erro ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {erro}
          </Alert>
        ) : null}
        <Field
          label="Quantidade de consultas"
          inputMode="numeric"
          required
          value={quantidade}
          onChange={(evento) => setQuantidade(evento.target.value)}
          error={erroQuantidade}
        />
        <Select
          label="Periodo"
          options={periodosDoLimite}
          value={periodo}
          onChange={(evento) =>
            setPeriodo(evento.target.value as BrokerConsultationPeriod)
          }
        />
        <label className="flex items-center gap-2 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={bloqueio}
            onChange={(evento) => setBloqueio(evento.target.checked)}
          />
          Bloqueio automatico ao atingir o limite
        </label>
        <label className="flex items-center gap-2 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={ativo}
            onChange={(evento) => setAtivo(evento.target.checked)}
          />
          Limite ativo
        </label>
        <Textarea
          label="Observacoes do limite"
          maxLength={2000}
          value={observacoes}
          onChange={(evento) => setObservacoes(evento.target.value)}
        />
      </form>
    </Modal>
  );
}

function UnblockDialog({
  open,
  enviando,
  onClose,
  onConfirmar,
}: {
  open: boolean;
  enviando: boolean;
  onClose: () => void;
  onConfirmar: (justificativa: string) => void;
}) {
  const [justificativa, setJustificativa] = useState('');
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Desbloquear consultas"
      description="O desbloqueio vale ate o fim do periodo corrente. A justificativa e quem desbloqueou ficam no cadastro e no historico."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            loading={enviando}
            disabled={justificativa.trim() === ''}
            onClick={() => onConfirmar(justificativa.trim())}
          >
            Desbloquear
          </Button>
        </>
      }
    >
      <Textarea
        label="Justificativa do desbloqueio"
        required
        maxLength={2000}
        value={justificativa}
        onChange={(evento) => setJustificativa(evento.target.value)}
      />
    </Modal>
  );
}

/**
 * Secao "Limite de consultas" em Dados cadastrais (COR-033). A tela mostra a contagem e
 * o que esta gravado. Com o bloqueio automatico ligado, o limite barra a consulta ao
 * bureau (ADR 0098); com ele desligado, so informa.
 */
export function BrokerConsultationLimitCard({ corretor }: { corretor: Broker }) {
  const { can, session } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const consulta = useLimiteDeConsultas(corretor.id);
  const [editando, setEditando] = useState(false);
  const [desbloqueando, setDesbloqueando] = useState(false);

  const daCorretora = session?.broker_scoped === true;
  const podeAlterar = can(PERMISSAO_LIMITE_CONSULTA) && !daCorretora;
  const podeDesbloquear = can(PERMISSAO_DESBLOQUEIO_CONSULTA) && !daCorretora;

  function atualizar(situacao: BrokerConsultationStatus) {
    queryClient.setQueryData(chaveDoLimiteDeConsultas(corretor.id), situacao);
    void queryClient.invalidateQueries({
      queryKey: ['entity-history', 'broker', corretor.id],
    });
  }

  const salvar = useMutation({
    mutationFn: (entrada: BrokerConsultationLimitInput) =>
      consulta.data?.configured
        ? brokers.replaceConsultationLimit(corretor.id, entrada)
        : brokers.createConsultationLimit(corretor.id, entrada),
    onSuccess: (situacao) => {
      toast.success('Limite de consultas salvo.');
      setEditando(false);
      atualizar(situacao);
    },
  });

  const desbloquear = useMutation({
    mutationFn: (justificativa: string) =>
      brokers.unblockConsultations(corretor.id, justificativa),
    onSuccess: (situacao) => {
      toast.success('Consultas desbloqueadas no periodo.');
      setDesbloqueando(false);
      atualizar(situacao);
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const situacao = consulta.data;
  const limite = situacao?.limit ?? null;

  return (
    <DetailCard
      title="Limite de consultas"
      description={PENDENCIA_LIMITE_CONSULTA}
      loading={consulta.isPending}
      error={consulta.error}
      action={
        podeAlterar && situacao ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              salvar.reset();
              setEditando(true);
            }}
          >
            {situacao.configured ? 'Alterar limite' : 'Cadastrar limite'}
          </Button>
        ) : undefined
      }
    >
      {situacao ? (
        <div className="flex flex-col gap-3">
          <dl className="grid gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs text-ink-500">Situacao no periodo</dt>
              <dd className="mt-0.5">{situacaoDoPeriodo(situacao)}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-500">Consultas no periodo</dt>
              <dd className="mt-0.5 font-medium text-ink-900">
                {limite ? `${situacao.used} de ${limite.max_queries}` : situacao.used}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-ink-500">Periodo</dt>
              <dd className="mt-0.5 text-ink-900">
                {limite ? rotuloDoPeriodo(limite.period) : 'Mensal'} (desde{' '}
                {formatDateTime(situacao.period_start)})
              </dd>
            </div>
            {limite ? (
              <>
                <div>
                  <dt className="text-xs text-ink-500">Bloqueio automatico</dt>
                  <dd className="mt-0.5 text-ink-900">
                    {limite.auto_block ? 'Ligado' : 'Desligado'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-500">Consultas restantes</dt>
                  <dd className="mt-0.5 text-ink-900">{situacao.remaining ?? '-'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-500">Ultimo desbloqueio</dt>
                  <dd className="mt-0.5 text-ink-900">
                    {limite.unblocked_at
                      ? `${formatDateTime(limite.unblocked_at)} por ${limite.unblocked_label || 'nao informado'}: ${limite.unblock_reason}`
                      : 'Nenhum'}
                  </dd>
                </div>
              </>
            ) : null}
          </dl>
          {limite?.notes ? (
            <p className="text-xs text-ink-700">Observacoes: {limite.notes}</p>
          ) : null}
          <p className="text-2xs text-ink-500">{PENDENCIA_CONTAGEM_CONSULTA}</p>
          {situacao.blocked && podeDesbloquear ? (
            <div>
              <Button size="sm" onClick={() => setDesbloqueando(true)}>
                Desbloquear consultas
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {editando ? (
        <LimitForm
          open
          atual={limite}
          enviando={salvar.isPending}
          erro={salvar.error?.message}
          onClose={() => setEditando(false)}
          onEnviar={(entrada) => salvar.mutate(entrada)}
        />
      ) : null}
      {desbloqueando ? (
        <UnblockDialog
          open
          enviando={desbloquear.isPending}
          onClose={() => setDesbloqueando(false)}
          onConfirmar={(justificativa) => desbloquear.mutate(justificativa)}
        />
      ) : null}
    </DetailCard>
  );
}

/**
 * Consulta da comissao contra o limite de comissao (GetBrokerComissionValidated,
 * COR-216). So compara e mostra o resultado; nada e bloqueado.
 */
export function BrokerCommissionCheckCard({ corretor }: { corretor: Broker }) {
  const [taxa, setTaxa] = useState('');
  const [resultado, setResultado] = useState<BrokerConsultationCommissionCheck | null>(
    null,
  );
  const erroTaxa = rateInputError(taxa);

  const validar = useMutation({
    mutationFn: (taxaBps: number) => brokers.commissionValidation(corretor.id, taxaBps),
    onSuccess: setResultado,
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const bps = parseRate(taxa);
    if (bps === null || erroTaxa) return;
    validar.mutate(bps);
  }

  const tom =
    resultado?.result === 'acima_do_limite'
      ? 'warning'
      : resultado?.result === 'dentro_do_limite'
        ? 'success'
        : 'info';

  return (
    <DetailCard
      title="Validacao de comissao"
      description={PENDENCIA_COMISSAO_VALIDACAO}
    >
      <form onSubmit={enviar} className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Field
            label="Taxa a validar (%)"
            inputMode="decimal"
            value={taxa}
            onChange={(evento) => {
              setTaxa(evento.target.value);
              setResultado(null);
            }}
            error={erroTaxa}
          />
        </div>
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          loading={validar.isPending}
          disabled={taxa.trim() === '' || erroTaxa !== undefined}
        >
          Validar
        </Button>
      </form>
      {validar.error ? (
        <Alert tone="danger" title="Nao foi possivel validar" className="mt-3">
          {validar.error.message}
        </Alert>
      ) : null}
      {resultado ? (
        <Alert
          tone={tom}
          title={resultadosDaComissao[resultado.result] ?? resultado.result}
          className="mt-3"
        >
          Taxa {formatRate(resultado.rate_bps)}
          {resultado.max_commission_bps !== null
            ? `; limite de comissao ${formatRate(resultado.max_commission_bps)}`
            : ''}
          {resultado.excess_bps !== null
            ? `; passa ${formatRate(resultado.excess_bps)} do limite`
            : ''}
          ; comissao acordada {formatRate(resultado.commission_bps)}. Nada e bloqueado.
        </Alert>
      ) : null}
    </DetailCard>
  );
}
